import React, { useState, FC, Dispatch, SetStateAction } from 'react';
import { useRouter } from 'next/router';
import sortBy from 'lodash/sortBy';
import { Address } from 'shared/types/account';
import { NgcErrorType } from 'shared/types/error';
import { ShippingMethod } from 'shared/types/ngcCart';
import NotFound from 'components/commercetools-ui/organisms/not-found';
import { useCart } from 'context';
import { parseNGCError } from 'helpers/ngc';
import { NgcErrorCodes } from 'helpers/utils/ngcResponseUtil';
import { useBusinessUnit } from 'frontastic/hooks';
import OrderReference from './order-reference';
import ShippingAddress from './shipping-address';
import ShippingMethods from './shipping-methods';
import styles from './shipping-wrapper.module.scss';
import { CheckoutError } from '../../index';

export interface ShippingWrapperProps {
  poNumber: string;
  setPoNumber: Dispatch<SetStateAction<string>>;
  poNumberError: CheckoutError;
}

export const sortData = (data: ShippingMethod[]) => {
  return sortBy(data, (shippingMethod) => shippingMethod.shippingPrice?.total);
};

const ShippingWrapper: FC<ShippingWrapperProps> = ({ poNumber, setPoNumber, poNumberError }) => {
  const initialError = {
    isError: false,
    callBack: null,
    showTryAgain: false,
  };
  const router = useRouter();
  const { cart, getShippingMethods, updateShippingAddress, updateShippingMethod, resetCart } = useCart();
  const [shippingAddressError, setShippingAddressError] = useState<CheckoutError>(initialError);
  const [shippingMethodError, setShippingMethodError] = useState<CheckoutError>(initialError);

  const { shippingAddresses } = useBusinessUnit();
  const [shippingMethodsListData, setShippingMethodsListData] = useState<ShippingMethod[]>();

  const [loadingShippingAddress, setLoadingShippingAddress] = useState(false);

  const [loadingShippingMethod, setLoadingShippingMethod] = useState(false);
  const [selectedShippingAddress, setSelectedShippingAddress] = useState<Address>();
  const [selectedShippingMethod, setSelectedShippingMethod] = useState<ShippingMethod>();
  const [apiFailureError, setApiFailureError] = useState(false);

  const shippingMethodErrorHandler = (error: NgcErrorType, callback: () => void) => {
    setLoadingShippingMethod(false);
    let showTryAgain = false;
    if (
      error?.code === NgcErrorCodes.CART_NOT_FOUND ||
      error?.code === NgcErrorCodes.OPERATION_NOT_PERMITTED ||
      error?.code === NgcErrorCodes.SHIPPING_ADDRESS_NULL
    ) {
      showTryAgain = true;
    } else if (error?.code === NgcErrorCodes.CART_ALREADY_ORDERED) {
      router.push('/something?isCartAlreadyOrdered=true');
    }
    setShippingMethodError({
      isError: true,
      callBack: () => {
        if (callback) {
          callback();
        }
      },
      showTryAgain,
    });
  };

  const fetchShippingMethods = async () => {
    setLoadingShippingMethod(true);
    setShippingMethodError(initialError);
    try {
      const shippingMethodsResponse = await getShippingMethods();
      const sortedData = sortData(shippingMethodsResponse);
      setShippingMethodsListData(sortedData);
      setLoadingShippingMethod(false);
    } catch (error) {
      const parsedError = parseNGCError(error);
      shippingMethodErrorHandler(parsedError, () => fetchShippingMethods());
    }
  };

  const updateShippingMethodToCartWrapper = async (shippingMethod: ShippingMethod) => {
    setLoadingShippingMethod(true);
    setShippingMethodError(initialError);
    if (shippingMethod !== undefined) {
      const shippingMethodId = shippingMethod.id as string;
      try {
        await updateShippingMethod(shippingMethodId);
        setSelectedShippingMethod(shippingMethod);
        setLoadingShippingMethod(false);
      } catch (error) {
        const parsedError = parseNGCError(error);
        shippingMethodErrorHandler(parsedError, () => updateShippingMethodToCartWrapper(shippingMethod));
      }
    }
  };

  const updateShippingAddressErrorHandler = (addressId: string, error: NgcErrorType) => {
    setLoadingShippingAddress(false);
    let showTryAgain = false;
    if (
      error?.code === NgcErrorCodes.CART_NOT_FOUND ||
      error?.code === NgcErrorCodes.OPERATION_NOT_PERMITTED ||
      error?.code === NgcErrorCodes.SHIPPING_ADDRESS_NULL
    ) {
      showTryAgain = true;
    } else if (error?.code === NgcErrorCodes.CART_ALREADY_ORDERED) {
      router.push('/something?isCartAlreadyOrdered=true');
    }
    setShippingAddressError({
      isError: true,
      callBack: () => {
        updateShippingAddressHandler(addressId, true);
      },
      showTryAgain,
    });
  };

  const updateShippingAddressHandler = async (addressId: string, isRetry = false, initialLoad = false) => {
    setLoadingShippingAddress(true);
    setShippingAddressError(initialError);
    if (initialLoad) {
      setShippingAddressOnInitialRender();
      return;
    }
    try {
      await updateShippingAddress(addressId, isRetry);
      setShippingAddressOnInitialRender();
    } catch (error) {
      const parsedError = parseNGCError(error);
      updateShippingAddressErrorHandler(addressId, parsedError);
    }
  };

  const setShippingAddressOnInitialRender = () => {
    setLoadingShippingAddress(false);
    setSelectedShippingAddress(shippingAddresses.find((val) => val.addressId === cart?.shippingAddress?.addressId));
    fetchShippingMethods();
  };

  return (
    <div className={styles.shippingWrapperContainer}>
      <div data-testid="shipping-information__address_test-id">
        <ShippingAddress
          onChangeHandler={updateShippingAddressHandler}
          isLoading={loadingShippingAddress}
          setSelectedShippingAddress={setSelectedShippingAddress}
          shippingAddressError={shippingAddressError}
          setShippingAddressError={setShippingAddressError}
        />
      </div>

      <div data-testid="shipping-information__methods_test-id">
        <ShippingMethods
          fetchShippingMethods={fetchShippingMethods}
          onChangeHandler={updateShippingMethodToCartWrapper}
          shippingMethodsListData={shippingMethodsListData || []}
          isLoading={loadingShippingMethod}
          selectedShippingMethod={selectedShippingMethod as ShippingMethod}
          setSelectedShippingMethod={setSelectedShippingMethod}
          shippingMethodError={shippingMethodError}
        />
      </div>
      <OrderReference setPoNumber={setPoNumber} poNumber={poNumber} poNumberError={poNumberError} />
    </div>
  );
};

export default ShippingWrapper;
